import { useEffect, useState, useCallback } from "react";
import { ArchiveRestore, Eraser, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

export const RecycleBin = ({ open, onOpenChange, onChanged }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/configs/bin");
      setItems(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load recycle bin");
    }
  }, []);

  useEffect(() => {
    if (open) refresh();
  }, [open, refresh]);

  const restore = async (id) => {
    try {
      await api.post(`/configs/bin/${id}/restore`);
      await refresh();
      onChanged?.();
      toast.success("Configuration restored");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Restore failed");
    }
  };

  const clearBin = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete("/configs/bin");
      await refresh();
      onChanged?.();
      toast.success(data.message);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not clear recycle bin");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#131924] border-[#243046] text-slate-200 max-w-lg" data-testid="recycle-bin-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono text-slate-100 flex items-center gap-2">
            <Trash2 className="h-5 w-5 text-amber-400" /> Recycle Bin
          </DialogTitle>
          <DialogDescription className="text-slate-400 text-xs font-mono">
            Deleted configurations are kept here until you clear the bin.
          </DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <p className="text-xs text-slate-500 font-mono py-6 text-center" data-testid="bin-empty">Recycle bin is empty.</p>
        ) : (
          <>
            <ul className="space-y-1.5 max-h-72 overflow-y-auto" data-testid="bin-list">
              {items.map((c, i) => (
                <li key={c.id} data-testid={`bin-row-${i}`} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#243046] bg-[#0D131E]">
                  <span className="flex-1 min-w-0">
                    <span className="block text-xs font-mono text-slate-200 truncate">{c.name}</span>
                    <span className="block text-[10px] font-mono text-slate-500">
                      {c.config?.orientation} · {c.config?.shape}
                      {c.deletedAt && ` · deleted ${new Date(c.deletedAt).toLocaleString()}`}
                    </span>
                  </span>
                  <button title="Restore" data-testid={`restore-config-btn-${i}`} onClick={() => restore(c.id)} className="p-1.5 rounded text-slate-400 hover:text-emerald-300 hover:bg-[#1A2232] transition-colors">
                    <ArchiveRestore className="h-3.5 w-3.5" />
                  </button>
                </li>
              ))}
            </ul>
            <Button
              data-testid="clear-bin-btn"
              onClick={clearBin}
              disabled={busy}
              variant="outline"
              className="w-full border-red-500/40 bg-transparent text-red-300 hover:bg-red-500/10 hover:text-red-200"
            >
              <Eraser />{busy ? "Clearing…" : `Clear Recycle Bin (${items.length})`}
            </Button>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
